import { ITermCourse, ISession } from '../../../../interfaces/scheduler_interfaces';
import { ISectionType } from '../../../../interfaces/scheduler_interfaces';
import { ScheduledSectionServant } from '../../../course_scheduler/servants/scheduled_section_servant';
import { ICommand } from 'core-ts-types';
import * as _4 from 'lodash4';
import { SectionSwapServant } from '../../../../swap/servants/section_swap_servant';
import { SectionSwapModel } from '../../../../swap/models/course_section_swap_model';
import { ITermSectionTypesDatasource } from '../../datasources/term_section_types_datasource';
import { CourseDetailsFacade } from '../../../course_details/facades/course_details_facade';

class MultiSectionCourseTile {
  sectionTypeMetadata: {
    scheduled: Record<string, boolean>;
    scheduledSectionTypes: string[];
  } = {
    scheduled: {},
    scheduledSectionTypes: []
  };
  throttledSectionStatusCheck: () => void;
  toggleSectionTypes: boolean;
  private ignoreFullStatus: boolean;
  private course: ITermCourse;
  private showSessionHeader: boolean = false;
  private isRescheduling: boolean = false;

  private scheduledSectionTypes: ISectionType[];
  constructor(
    private $state: angular.ui.IStateService,
    private $stateParams: ng.ui.IStateParamsService,
    private ngScheduledSectionServant: ScheduledSectionServant,
    private ngCourseDetailsFacade: CourseDetailsFacade,
    private ngSectionSwapServant: SectionSwapServant,
    private updateSwapFromSectionTypeCommand: ICommand,
    private ngCourseSectionSwapModel: SectionSwapModel,
    private ngTermSectionTypesDatasource: ITermSectionTypesDatasource
  ) {
    this.throttledSectionStatusCheck = _4.throttle(this.checkSectionTypeStatus, 3000, { trailing: false });
  }
  $onInit() {
    this.showSessionHeader = _4.gt(
      _4.filter(this.course._session_section_types, (session: ISession) => session.session !== '-1').length,
      0
    );
    const courseIdFromParams: string = _4.get(this.$state, '__previousRoute__.params.courseId');
    this.toggleSectionTypes = _4.isEqual(this.course.id, _4.parseInt(courseIdFromParams));
  }

  isSessionActive(sessionId: string) {
    if (_4.isEmpty(this.scheduledSectionTypes)) {
      return true;
    }

    if (this.ngSectionSwapServant.isInSwapMode()) {
      return _4.toString(this.ngCourseSectionSwapModel.read().session) === _4.toString(sessionId);
    }
    if (this.areAllsectionsScheduledinSession(sessionId)) {
      return false;
    }
    const isSessionScheduled = _4.includes(_4.map(this.scheduledSectionTypes, 'session'), _4.toNumber(sessionId));
    return this.isRescheduling ? !isSessionScheduled : isSessionScheduled;
  }

  areAllsectionsScheduledinSession(sessionId: string) {
    const sectionTypeIdsforSession = _4.sortBy(
      _4.map(
        _4.get(
          _4.first(_4.filter(this.course._session_section_types, (session: ISession) => session.session === sessionId)),
          'section_types'
        ) as ISectionType[],
        'id'
      )
    );

    const allScheduledSectionTypeIds = _4.sortBy(_4.map(this.scheduledSectionTypes, 'id'));
    const areAllSectionsScheduled = allScheduledSectionTypeIds.length >= sectionTypeIdsforSession.length;
    const allSectionsForSessionScheduled = sectionTypeIdsforSession.every((sectionId) =>
      allScheduledSectionTypeIds.includes(sectionId)
    );
    return areAllSectionsScheduled && allSectionsForSessionScheduled;
  }

  showSession(sessionId: string): boolean {
    if (this.ngSectionSwapServant.isInSwapCourseMode()) {
      return true;
    }
    if (this.ngSectionSwapServant.isInSwapMode()) {
      return _4.toString(this.ngCourseSectionSwapModel.read().session) === _4.toString(sessionId);
    }
    return true;
  }

  isSessionEnabled(sessionId: string, sectionType: ISectionType) {
    let scheduledSessions: any = [];
    if (_4.isEmpty(this.scheduledSectionTypes)) {
      return true;
    }
    scheduledSessions = _4.keys(_4.groupBy(this.scheduledSectionTypes, 'session'));
    const isSectionTypeOfSameSessionScheduled = _4.find(this.scheduledSectionTypes, {
      session: parseInt(sessionId, 10),
      id: sectionType.id
    });
    if (this.isRescheduling) {
      if (_4.includes(scheduledSessions, sessionId) && isSectionTypeOfSameSessionScheduled) {
        return false;
      }
      return true;
    }

    const isSessionSectionTypeEnabled = _4.includes(scheduledSessions, sessionId);
    if (isSessionSectionTypeEnabled) {
      const isSessionSectionTypeScheduled = this.isSectionTypeScheduled(sectionType);
      return !isSessionSectionTypeScheduled;
    }
    return isSessionSectionTypeEnabled;
  }

  $onChanges() {
    this.checkSectionTypeStatus();
    this.scheduledSectionTypes = _4.filter(this.course._section_types, (sectionType: ISectionType) =>
      this.ngScheduledSectionServant.isSectionTypeScheduled(sectionType)
    );
  }

  getTruncatedString(str: string) {
    return _4.truncate(str, { length: 25 });
  }

  isSectionTypeScheduled(sectionType: ISectionType) {
    // Throttle this check - expensive to be on a view.
    this.throttledSectionStatusCheck();
    return this.sectionTypeMetadata.scheduled[sectionType.id];
  }

  isSectionTypeBadgeScheduled(sectionType: ISectionType) {
    return _4.includes(this.sectionTypeMetadata.scheduledSectionTypes, sectionType.name);
  }

  showCheckMark(sectionType: ISectionType) {
    return this.sectionTypeMetadata.scheduled[sectionType.id];
  }

  getAriaLabel(sectionType: any, course: any) {
    return this.isSectionTypeScheduled(sectionType)
      ? `Scheduled section ${sectionType.name} for ${course.title} Course`
      : `Click to View ${sectionType.name} Sections for ${course.title} Course`;
  }

  goToSectionGroup(sectionType: any, session: string) {
    if (!this.isSessionEnabled(session, sectionType)) {
      return;
    }
    if (this.isSectionTypeScheduled(sectionType)) {
      return;
    }
    if (this.ngSectionSwapServant.isInSwapMode()) {
      this.$state.go('my.schedule.registration.swap.courseSections', {
        courseId: sectionType.course,
        sectionTypeId: sectionType.id,
        ignoreConflicts: true,
        swapCourse: this.$stateParams.swapCourse === 'true'
      });
      this.updateSwapFromSectionTypeCommand(sectionType);
    } else {
      this.$state.go('my.schedule.registration.courseSections', {
        courseId: sectionType.course,
        sectionTypeId: sectionType.id
      });
    }
  }

  courseTilePressed() {
    this.toggleSectionTypes = !this.toggleSectionTypes;
  }

  openCourseDetail() {
    this.ngCourseDetailsFacade.openCourseDetails(this.course.nk);
  }

  private handleExpandToggleKeyDown($event: KeyboardEvent) {
    if ($event.key === 'Enter' || $event.key === ' ') {
      this.courseTilePressed();
    }
  }

  private checkSectionTypeStatus() {
    if (this.ngSectionSwapServant.isInSwapMode()) {
      this.swapCheckSectionTypeStatus();
      return;
    }

    _4.each(this.course._section_types, (sectionType: ISectionType) => {
      if (this.ignoreFullStatus) {
        this.sectionTypeMetadata.scheduled[sectionType.id] = false;
      } else {
        this.sectionTypeMetadata.scheduled[sectionType.id] =
          this.ngScheduledSectionServant.isSectionTypeScheduled(sectionType);
      }
    });
    const selectedSectionTypeIdsforSession = _4.compact(
      _4.map(this.sectionTypeMetadata.scheduled, (value: boolean, key: string) => value && key)
    );
    const selectedSectionTypeNames: string[] = _4.compact(
      _4.map(_4.values(_4.pick(this.ngTermSectionTypesDatasource.read(), selectedSectionTypeIdsforSession)), 'name')
    );
    this.sectionTypeMetadata.scheduledSectionTypes = selectedSectionTypeNames;
    // each this.course._section_types_by_session
    //  sessionGroup.disabled = (sessionGroup.id != plannedSessionId)
    // Above code tells you which section type is planned.
    // That secition type has session id.
    // Use that session id to enable that session group and disabled others.
  }

  private swapCheckSectionTypeStatus() {
    _4.each(this.course._section_types, (sectionType: ISectionType) => {
      const isSectionTypeSelectedForSwap = !_4.isEmpty(
        _4.find(this.ngCourseSectionSwapModel.read().swap_to_section_ids, { section_type: sectionType.id })
      );
      this.sectionTypeMetadata.scheduled[sectionType.id] = isSectionTypeSelectedForSwap;
    });
  }
}

class MultiSectionCourseTileComponent implements ng.IComponentOptions {
  public bindings: any;
  public controller: any;
  public templateUrl: string;
  constructor() {
    this.bindings = {
      course: '<',
      ignoreFullStatus: '@',
      isRescheduling: '<?',
      opened: '@'
    };
    this.controller = MultiSectionCourseTile;
    this.templateUrl =
      'capability-ng-scheduler/src/views/term_courses/components/multi_section_course_tile/multi_section_course_tile.html';
  }
}
angular.module('capability-ng-scheduler').component('multiSectionCourseTile', new MultiSectionCourseTileComponent());



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/term_courses/components/multi_section_course_tile/multi_section_course_tile_component.ts